import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import useSWR from 'swr';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { AppContext } from 'Context';
import { Page, Text } from 'Components';
import { dataFetcher, endpoints } from 'Api';
import { Pathname } from 'Routes';
import { ReactComponent as AddIcon } from 'Assets/add.svg';
import { ReactComponent as DragIcon } from 'Assets/drag.svg';
import { ReactComponent as EditIcon } from 'Assets/edit.svg';
import { ReactComponent as DropdownIcon } from 'Assets/chevron-down.svg';
import { Menu, MenuItem } from '@material-ui/core';
import styles from './index.module.css';
import CreateMenuDropdown from './CreateMenuDropdown';

const CategoryIDDropdown = ({ onSelect }) => {
  const [options, setOptions] = useState([]);

  // the following useSWR hook's first parameter needs to be an array in this case
  const { isValidating: fetchingCategories, mutate: fetchCategories } = useSWR([endpoints.getMyCategory], {
    fetcher: (url) => dataFetcher(url),
    onSuccess: ({ success, data }) => {
      if (success) {
        setOptions(data?.map(({ title: name, id: value }) => ({ name, value })));
      }
    },
    onError: () => { },
  });

  useEffect(() => { fetchCategories(); }, []);

  return (
    <CreateMenuDropdown
      alignRight
      isLoading={fetchingCategories}
      defaultName="Select Category"
      onSelect={onSelect}
      options={options}
    />
  );
};

const CategoryCreateButton = ({ buttonLabel, isCompact, onCreate }) => {
  const { setAppSnackbar, langText, setCategoryRefreshTrigger } = useContext(AppContext);
  const commonText = langText?.common;
  const [categoryID, setCategoryID] = useState(undefined);
  const [categoryType, setCategoryType] = useState('category');
  const [val, setVal] = useState(undefined);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [creating, setCreatingStatus] = useState(false);

  const { mutate: addNewCategory } = useSWR([categoryType === 'category'
    ? endpoints.addEditCategory : endpoints.addUpdateMySubCategory, val, categoryID, onCreate], {
    fetcher: (url, categoryName) => dataFetcher(url, {
      title: categoryName,
    }),
    onSuccess: ({ success, message }) => {
      if (success) {
        setCreatingStatus(false);
        setVal(undefined);
        setCategoryType('category');
        setCategoryID(undefined);
        setMenuOpen(false);
        // Trigger category refresh globally
        setCategoryRefreshTrigger((prev) => prev + 1);
        if (categoryType === 'category') {
          if (onCreate) {
            onCreate();
          }
        }
        setAppSnackbar({
          isVisible: true,
          message: message || `${categoryType === 'sub-category' ? 'Sub-Category' : 'Category'} '${val}' was created`,
        });
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while creating your category' });
      }
    },
    onError: () => {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while creating your category' });
    },
  });
  const handleCreate = () => {
    setCreatingStatus(true);
    addNewCategory();
  };
  useEffect(() => { setCategoryID(undefined); }, [categoryType]);

  useEffect(() => { setVal(undefined); setCategoryType('category'); setCategoryID(undefined); }, [setMenuOpen]);

  return (
    <div className={styles.createButtonWrapper}>
      <button
        className={cn({ [styles.createButton]: true, [styles.compact]: isCompact })}
        onClick={() => { setMenuOpen(true); }}
      >
        <span>{buttonLabel}</span>
        {!isCompact && <AddIcon className={styles.addIcon} />}
      </button>
      {isMenuOpen && (
        <div className={styles.createMenuContainer}>
          <div
            className={styles.createButtonScrim}
            onClick={() => { if (!creating) { setCategoryType('category'); setMenuOpen(false); } }}
            onKeyPress={undefined}
            role="button"
            tabIndex={0}
          />
          <div className={styles.createMenu}>
            <div className={styles.createMenuTitle}>
              <div className={styles.categoryDropdownContainer}>
                <CreateMenuDropdown
                  defaultName="New Category"
                  defaultValue={categoryType}
                  onSelect={setCategoryType}
                  options={[
                    { name: 'New Category', value: 'category' },
                    { name: 'New Sub Category', value: 'sub-category' },
                  ]}
                />
                {/* New Category
                {(categoryType === 'sub-category') && <CategoryIDDropdown onSelect={setCategoryID} />} */}
              </div>
            </div>
            <input
              className={styles.createMenuInput}
              defaultValue={val}
              onInput={(e) => { setVal(e.target.value); }}
              maxLength="70"
            />
            <div className={styles.createMenuButtons}>
              <div className={styles.createMenuButtonsRight}>
                <button
                  className={styles.createMenuCancelButton}
                  disabled={creating}
                  onClick={() => { setCategoryType('category'); setMenuOpen(false); }}
                >
                  {commonText?.cancel || 'Cancel'}
                </button>
                <button
                  className={styles.createMenuCreateButton}
                  disabled={!val || !(val?.trim()) || creating}
                  onClick={handleCreate}
                >
                  {creating ? `${(commonText?.creating || 'Creating')}...` : (commonText?.create || 'Create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default CategoryCreateButton;
